import type { PetCompanion, PetMatureBenefitId } from '../domain/pet.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { Roller } from '../rules/dice.ts';
import { isOnCooldown, startCooldown } from '../rules/encounter.ts';

export type ExecutablePetMatureBenefitId = 'earthquake' | 'wrecking-ball';

export function petMatureAbilitySourceId(petId: string, benefit: ExecutablePetMatureBenefitId): string {
  return `pet:${petId}:mature:${benefit}`;
}

export function petMatureAbilityProblem(
  pet: Pick<PetCompanion, 'id' | 'healthBarSlotsRemaining' | 'attitude' | 'level' | 'matureBenefit'>,
  benefit: PetMatureBenefitId,
  runtime: RuntimeState | null,
): string | null {
  if (pet.healthBarSlotsRemaining <= 0) return 'A dead companion cannot use a maturity benefit.';
  if (pet.attitude !== 'bonded') return 'Only a Bonded pet can use a maturity benefit as a companion.';
  if (pet.level < 15 || pet.matureBenefit?.id !== benefit) return 'This pet does not have that Level-15 maturity benefit.';
  if ((benefit === 'earthquake' || benefit === 'wrecking-ball') && runtime && isOnCooldown(runtime, petMatureAbilitySourceId(pet.id, benefit))) {
    return `${benefit === 'earthquake' ? 'Earthquake' : 'Wrecking Ball'} is on cooldown.`;
  }
  return null;
}

export function rollPetEarthquake(roller: Roller): { die: number; triggers: boolean } {
  const die = roller.roll(12);
  return { die, triggers: die >= 10 };
}

export function startPetEarthquakeCooldown(petId: string, runtime: RuntimeState): RuntimeState {
  return startCooldown(runtime, { sourceId: petMatureAbilitySourceId(petId, 'earthquake'), scope: 'hours', amount: 5 });
}

/**
 * Earthquake's five-hour cooldown begins only when the 1d12 actually triggers
 * the Earthquake on 10+. A failed trigger is not an activated Earthquake and
 * may be checked again after a later qualifying Move -> melee Attack.
 */
export function applyPetEarthquakeCooldownAfterRoll(
  petId: string,
  runtime: RuntimeState,
  triggers: boolean,
): RuntimeState {
  return triggers ? startPetEarthquakeCooldown(petId, runtime) : runtime;
}

export function rollPetWreckingBallDamage(
  roller: Roller,
  pet: Pick<PetCompanion, 'size'>,
  floor: number,
): { total: number; faces: number[]; floorBonus: number } {
  if (!Number.isInteger(floor) || floor < 1) throw new Error('Floor must be a whole number of at least 1.');
  const faces = Array.from({ length: pet.size }, () => roller.roll(6));
  return { total: faces.reduce((sum, face) => sum + face, 0) + floor, faces, floorBonus: floor };
}

export function startPetWreckingBallCooldown(petId: string, runtime: RuntimeState): RuntimeState {
  return startCooldown(runtime, { sourceId: petMatureAbilitySourceId(petId, 'wrecking-ball'), scope: 'day' });
}
